import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Row, Col, Table, Input, Select, Button, Modal, notification, Spin, Tag, Grid, Tooltip } from 'antd';
import withRoleAccess from '../../hoc/withRoleAccess';
import Sidebar from '../../components/Sidebar/Sidebar';
import Header from '../../components/Header/Header';
import { ExclamationCircleOutlined, UserOutlined, InfoCircleOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import '../Dashboard.css';
import { useTranslation } from 'react-i18next';

const { Option } = Select;
const { confirm } = Modal;
const { useBreakpoint } = Grid;

// Role styling configuration
const roleStyles = {
  superadmin: {
    color: '#003366', // dark blue
    textColor: '#ffffff'
  },
  admin: {
    color: '#ff8c00', // orange
    textColor: '#ffffff'
  },
  teacher: {
    color: '#006400', // dark green
    textColor: '#ffffff'
  },
  student: {
    color: '#8b0000', // dark red
    textColor: '#ffffff'
  }
};

const UserControlPage = () => {
  const { t, i18n } = useTranslation();
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [branches, setBranches] = useState([]);
  const [branchFilter, setBranchFilter] = useState('');
  const [studentBranchStatusCounts, setStudentBranchStatusCounts] = useState({});
  const [activeCounts, setActiveCounts] = useState({
    superadmin: { total: 0, active: 0 },
    admin: { total: 0, active: 0 },
    teacher: { total: 0, active: 0 },
    student: { total: 0, active: 0 },
    total: { total: 0, active: 0 },
  });
  const API_URL = process.env.REACT_APP_API_URL;
  const MAX_ACTIVE_USERS = {
    superadmin: 10,
    admin: 30,
    teacher: 120,
    student: 1000,
  };
  const ITEMS_PER_PAGE = 50;
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);

  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [newUserData, setNewUserData] = useState({
    firstname: '',
    lastname: '',
    nickname: '',
    email: '',
    password: '',
    role: 'admin',
  });

  const [isEditing, setIsEditing] = useState(false);
  const [editingUserId, setEditingUserId] = useState(null);


  const formatDateTime = (dateTimeString) => {
    if (!dateTimeString) return t('neversignin');
    const date = new Date(dateTimeString);
    if (isNaN(date.getTime())) return t('neversignin');
    const options = { day: '2-digit', month: 'short', year: 'numeric' };
    const formattedDate = new Intl.DateTimeFormat(i18n.language || 'en-UK', options).format(date).toUpperCase();
    const today = new Date();
    const diffTime = Math.abs(today - date);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    return `${formattedDate} (${diffDays} ${t('daysAgo')})`;
};

// First, define refreshToken
const refreshToken = useCallback(async () => {
  const refreshToken = localStorage.getItem('refreshToken');
  const response = await axios.post(`${API_URL}/refresh-token`, { refreshToken });
  localStorage.setItem('token', response.data.accessToken);
}, [API_URL]);

// Define calculateCounts with useCallback
const calculateCounts = useCallback((users) => {
  const counts = {
    superadmin: { total: 0, active: 0 },
    admin: { total: 0, active: 0 },
    teacher: { total: 0, active: 0 },
    student: { total: 0, active: 0 },
    total: { total: 0, active: 0 },
  };
  users.forEach(user => {
    if (counts.hasOwnProperty(user.role)) {
      counts[user.role].total += 1;
      if (user.status === 'active') counts[user.role].active += 1;
    }
    counts.total.total += 1;
    if (user.status === 'active') counts.total.active += 1;
  });
  setActiveCounts(counts);
}, []);

const calculateStudentBranchStatusCounts = useCallback((users) => {
  const counts = {};

  users.forEach(user => {
    if (user.role === 'student') {
      const branch = user.branch_name || 'No Branch';
      if (!counts[branch]) {
        counts[branch] = { active: 0, inactive: 0 };
      }
      if (user.status === 'active') {
        counts[branch].active += 1;
      } else {
        counts[branch].inactive += 1;
      }
    }
  });

  setStudentBranchStatusCounts(counts);
}, []);



// Add a function reference object that will hold our functions
const functionRef = useRef({
  fetchUsers: null,
  handleTokenError: null
});

// Define handleTokenError first, using the function reference
functionRef.current.handleTokenError = (error) => {
  if (error.response && error.response.status === 401) {
    refreshToken()
      .then(() => functionRef.current.fetchUsers())
      .catch(err => console.error('Token refresh failed:', err));
  }
};

// Now wrap handleTokenError in useCallback to make it stable
const handleTokenError = useCallback((error) => {
  functionRef.current.handleTokenError(error);
}, []);

// Define fetchUsers using the stable handleTokenError
const fetchUsers = useCallback(async () => {
  try {
    const token = localStorage.getItem('token');
    const [usersResponse, lastSigninResponse] = await Promise.all([
      axios.get(`${API_URL}/users`, { headers: { Authorization: `Bearer ${token}` } }),
      axios.get(`${API_URL}/users/last-signin`, { headers: { Authorization: `Bearer ${token}` } }),
    ]);

    const lastSigninMap = lastSigninResponse.data.reduce((acc, curr) => {
      acc[curr.user_id] = curr.last_signin;
      return acc;
    }, {});

    const usersWithLastSignin = usersResponse.data.map(user => ({
      ...user,
      lastSignin: lastSigninMap[user.userid] || t('neversignin'),
    }));

    const sortedUsers = usersWithLastSignin.sort((a, b) => a.userid - b.userid);
    setUsers(sortedUsers);
    setFilteredUsers(sortedUsers);
    calculateCounts(sortedUsers);
    calculateStudentBranchStatusCounts(sortedUsers);
    setLoading(false);
  } catch (error) {
    console.error('Error fetching users:', error);
    handleTokenError(error);
    setLoading(false);
  }
}, [API_URL, t, calculateCounts, handleTokenError]);

useEffect(() => {
  const fetchBranches = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/branch`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setBranches(response.data);
    } catch (error) {
      console.error('Error fetching branches:', error);
    }
  };
  fetchBranches();
}, [API_URL]);

// Update the function reference whenever fetchUsers changes
functionRef.current.fetchUsers = fetchUsers;

// Initial data fetch
useEffect(() => {
  fetchUsers();
}, [fetchUsers]);


useEffect(() => {
  const results = users.filter(user => {
    const matchesSearchTerm =
      `${user.firstname} ${user.lastname}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesRole = roleFilter ? user.role === roleFilter : true;
    const matchesBranch = branchFilter ? user.branch_name === branchFilter : true;
    const matchesStatus = statusFilter ? user.status === statusFilter : true;

    return matchesSearchTerm && matchesRole && matchesBranch && matchesStatus;
  });

  setFilteredUsers(results);
  setCurrentPage(1);
}, [searchTerm, roleFilter, branchFilter, statusFilter, users]);



  const updateUserStatus = (userId, status, role) => {
    if (status === 'active' && activeCounts[role].active >= MAX_ACTIVE_USERS[role]) {
      notification.warning({
        message: t('alert'),
        description: t('maxLimitReached', { role: t(role), limit: MAX_ACTIVE_USERS[role] }),
      });
      return;
    }

    confirm({
      title: t('confirmStatusChange'),
      icon: <ExclamationCircleOutlined />,
      onOk: async () => {
        try {
          const token = localStorage.getItem('token');
          await axios.put(`${API_URL}/users/${userId}/status`, { status }, {
            headers: { Authorization: `Bearer ${token}` },
          });
          fetchUsers();
        } catch (error) {
          console.error('Error updating user status:', error);
          handleTokenError(error);
        }
      },
      onCancel() {},
    });
  };

 const resetPassword = (userId) => {
  confirm({
    title: t('confirmResetPassword'),
    icon: <ExclamationCircleOutlined />,
    onOk: async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.put(`${API_URL}/users/${userId}/reset-password`, {}, {
          headers: { Authorization: `Bearer ${token}` },
        });

        notification.success({
          message: t('temporaryPasswordTitle'),
          description: t('temporaryPassword', {
            userId,
            tempPassword: response.data.tempPassword,
          }),
        });
      } catch (error) {
        console.error('❌ Error resetting password:', error);

        if (error.response) {
          console.error('🔴 Response data:', error.response.data);
          console.error('🔴 Status:', error.response.status);
          console.error('🔴 Headers:', error.response.headers);
        } else if (error.request) {
          console.error('📡 No response received. Request was:', error.request);
        } else {
          console.error('⚠️ Request setup error:', error.message);
        }

        handleTokenError(error);
        notification.error({
          message: t('resetFailed'),
          description: error.response?.data?.message || 'An unexpected error occurred.',
        });
      }
    },
    onCancel() {},
  });
};

  const deleteUser = (user) => {
    confirm({
      title: t('Confirm Delete User'),
      icon: <ExclamationCircleOutlined />,
      content: (
        <div>
          <p>
            {t('Are you sure you want to permanently delete this user?')}
          </p>
          <p style={{ marginTop: 8 }}>
            <strong>{user.firstname} {user.lastname}</strong> ({user.email}) — {t(user.role)}
          </p>
          <p style={{ color: '#cf1322', marginTop: 8 }}>
            {t('This action cannot be undone.')}
          </p>
        </div>
      ),
      okText: t('delete'),
      okType: 'danger',
      cancelText: t('cancel'),
      onOk: async () => {
        try {
          const token = localStorage.getItem('token');
          await axios.delete(`${API_URL}/users/${user.userid}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          notification.success({ message: t('User deleted successfully') });
          fetchUsers();
        } catch (error) {
          // 409 = FK blockers — show the reason
          if (error.response?.status === 409) {
            const blockers = error.response.data?.blockers || [];
            const description = blockers.length > 0 ? (
              <div>
                <p>{t('This user is still referenced by:')}</p>
                <ul style={{ marginTop: 4, paddingLeft: 20 }}>
                  {blockers.map((b) => (
                    <li key={`${b.table}-${b.column}`}>
                      <strong>{b.count}</strong> {t('record(s) in')} <code>{b.table}</code>
                    </li>
                  ))}
                </ul>
                <p style={{ marginTop: 8 }}>
                  {t('Remove or reassign those records first, then try again.')}
                </p>
              </div>
            ) : (error.response.data?.detail || t('User is referenced by other records.'));

            notification.error({
              message: t('Cannot Delete User'),
              description,
              duration: 0, // keep open until dismissed
            });
            return;
          }
          console.error('Error deleting user:', error);
          handleTokenError(error);
          notification.error({
            message: t('Delete Failed'),
            description: error.response?.data?.message || 'Unexpected error occurred',
          });
        }
      },
    });
  };

  const branchColors = {
  1: "blue",
  2: "green",
  3: "magenta", // Ant Design supports "magenta" (nice pink)
};

 
  const screens = useBreakpoint();

  // Responsive role count box styles
  const getRoleBoxStyle = (role) => ({
    padding: screens.xs ? '10px' : '15px',
    borderRadius: '8px',
    backgroundColor: roleStyles[role]?.color,
    color: roleStyles[role]?.textColor,
    marginRight: screens.xs ? '8px' : '15px',
    marginBottom: screens.xs ? '8px' : '15px',
    minWidth: screens.xs ? '160px' : '200px',
    width: screens.xs ? '100%' : 'auto',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    fontSize: screens.xs ? '14px' : '16px'
  });

  // Responsive table columns
  const getResponsiveColumns = () => {
    const baseColumns = [
      {
        title: t('userId'),
        dataIndex: 'userid',
        key: 'userid',
        width: screens.xs ? 70 : 'auto'
      },
      {
  title: t('branch'),
  dataIndex: 'branch_name',
  key: 'branch_name',
  width: screens.xs ? 120 : 'auto',
  render: (branchName, record) => {
    // record.branch_id should be available in your dataset
    const color = branchColors[record.branch_id] || "default";

    return (
      <Tag
        color={color}
        style={{
          padding: screens.xs ? '2px 4px' : '4px 8px',
          fontSize: screens.xs ? '12px' : '14px',
          border: 'none'
        }}
      >
        {branchName || t('-')}
      </Tag>
    );
  }
},
      {
        title: screens.xs ? '' : t('name'),
        dataIndex: 'firstname',
        key: 'firstname',
        render: (text, user) => (
          <Tooltip 
            title={
              <div>
                <p><strong>{t('address')}:</strong> {user.address}</p>
                <p><strong>{t('phone')}:</strong> {user.phone}</p>
              </div>
            }
            placement="topLeft"
            overlayStyle={{ 
              backgroundColor: 'rgba(255, 255, 255, 0.8)', 
              color: 'rgba(0, 0, 0, 0.85)'
            }}
            overlayInnerStyle={{
              backgroundColor: 'rgba(255, 255, 255, 0.8)'
            }}
          >
            <span>
              {screens.xs && <UserOutlined style={{ marginRight: 5 }} />}
              {`${user.firstname} ${user.lastname}`}
            </span>
          </Tooltip>
        )
      },
      {
        title: t('userRole'),
        dataIndex: 'role',
        key: 'role',
        render: (role) => (
          <Tag
            color={roleStyles[role]?.color}
            style={{
              color: roleStyles[role]?.textColor,
              padding: screens.xs ? '2px 4px' : '4px 8px',
              fontSize: screens.xs ? '12px' : '14px',
              border: 'none'
            }}
          >
            {t(role)}
          </Tag>
        )
      },
      {
        title: t('status'),
        dataIndex: 'status',
        key: 'status',
        width: screens.xs ? 100 : 'auto',
        render: (status, user) => (
          <Select
            value={status}
            onChange={(value) => updateUserStatus(user.userid, value, user.role)}
            className={status === 'active' ? 'select-active' : 'select-inactive'}
            style={{ width: screens.xs ? '90px' : '120px' }}
            size={screens.xs ? 'small' : 'middle'}
          >
            <Option value="active">{t('active')}</Option>
            <Option value="inactive">{t('inactive')}</Option>
          </Select>
        )
      },
      {
        title: t('edit'),
        key: 'edit',
        render: (text, user) => {
          const currentUserRole = localStorage.getItem('role'); // or get from context/store
          const canEdit = ['admin', 'superadmin'].includes(currentUserRole);
          const isTargetAdmin = ['admin', 'superadmin'].includes(user.role);

          if (canEdit && isTargetAdmin && user.userid !== 1) {
            return (
              <Button
                onClick={() => {
                  setNewUserData({
                    firstname: user.firstname,
                    lastname: user.lastname,
                    nickname: user.nickname,
                    email: user.email,
                    password: '', // leave blank for now
                    role: user.role,
                  });
                  setIsEditing(true);
                  setEditingUserId(user.userid);
                  setCreateModalVisible(true);
                }}
              >
                <EditOutlined />
              </Button>
            );
          }

          return null;
        }
      }


    ];
  
    // Add additional columns for larger screens
    if (!screens.xs) {
      baseColumns.splice(2, 0, 
        {
          title: t('nickname'),
          dataIndex: 'nickname',
          key: 'nickname'
        },
        {
          title: t('email'),
          dataIndex: 'email',
          key: 'email'
        },
        {
          title: t('lastSignin'),
          dataIndex: 'lastSignin',
          key: 'lastSignin',
          render: (value) => formatDateTime(value)
        }
      );
      
      // Add info icon with tooltip for mobile-hidden information
      baseColumns.push({
        title: t('info'),
        key: 'info',
        render: (text, user) => (
          <Tooltip 
            title={
              <div>
                <p><strong>{t('address')}:</strong> {user.address}</p>
                <p><strong>{t('phone')}:</strong> {user.phone}</p>
              </div>
            }
            overlayStyle={{ 
              backgroundColor: 'rgba(255, 255, 255, 0.8)', 
              color: 'rgba(0, 0, 0, 0.85)'
            }}
            overlayInnerStyle={{
              backgroundColor: 'rgba(255, 255, 255, 0.8)'
            }}
          >
            <InfoCircleOutlined style={{ color: '#1890ff', cursor: 'pointer' }} />
          </Tooltip>
        )
      });
      
      baseColumns.push({
        title: t('resetPassword'),
        key: 'resetPassword',
        render: (text, user) => (
          user.userid !== 1 && (
            <Button
              onClick={() => resetPassword(user.userid)}
              size={screens.sm ? 'middle' : 'small'}
              danger
              type="primary"
              className="danger-button"
            >
              {t('resetPassword')}
            </Button>
          )
        )
      });

      baseColumns.push({
        title: t('delete'),
        key: 'delete',
        render: (text, user) => (
          user.userid !== 1 && (
            <Tooltip title={t('Delete User')}>
              <Button
                onClick={() => deleteUser(user)}
                size={screens.sm ? 'middle' : 'small'}
                danger
                shape="circle"
                icon={<DeleteOutlined />}
              />
            </Tooltip>
          )
        )
      });
    }
  
    return baseColumns;
  };

  // Responsive role counts section
  const renderRoleCounts = () => (
    <Row gutter={[8, 8]} style={{ marginBottom: '24px' }}>
      {Object.keys(MAX_ACTIVE_USERS).map((role) => {
        const remainingUsers = MAX_ACTIVE_USERS[role] - activeCounts[role].active; // Calculate remaining users
        const getTagColor = (remaining) => {
          if (remaining <= 0) return 'red'; // No remaining users
          if (remaining <= MAX_ACTIVE_USERS[role] * 0.2) return 'orange'; // Less than 20% remaining
          return 'green'; // Sufficient remaining users
        };
  
        return (
          <Col xs={24} sm={12} md={6} key={role}>
            <div style={getRoleBoxStyle(role)}>
              <strong>{t(role)}</strong>
              <div style={{ marginTop: '8px' }}>
                {t('maxUser')}: {MAX_ACTIVE_USERS[role]}
              </div>
              <div>
                {t('activeUser')}: {activeCounts[role].active}
              </div>
              <div>
                {t('inactiveUser')}: {activeCounts[role].total - activeCounts[role].active}
              </div>
              {/* Add remaining user tag at the bottom */}
              <div
                style={{
                  marginTop: '12px',
                  paddingTop: '12px',
                  borderTop: '1px solid rgba(0,0,0,0.06)',
                  textAlign: 'center',
                }}
              >
                <Tag
                  color={getTagColor(remainingUsers)} // Use dynamic color based on remaining users
                  style={{
                    padding: '4px 8px',
                    fontSize: '14px',
                  }}
                >
                  {t('Remaining Users')}: {remainingUsers}
                </Tag>
              </div>
            </div>
          </Col>
        );
      })}
    </Row>
  );
  

const renderStudentBranchStatusCounts = () => {
  const branchColors = [
    '#553c9a', // Purple
    '#0891b2', // Cyan
    '#c2410c'  // Orange-red
  ];

  return (
    <Row gutter={[8, 8]} style={{ marginBottom: '24px' }}>
      {Object.keys(studentBranchStatusCounts).map((branch, index) => (
        <Col xs={24} sm={12} md={6} key={branch}>
          <div style={{
            padding: screens.xs ? '10px' : '15px',
            borderRadius: '8px',
            backgroundColor: branchColors[index % branchColors.length],
            color: '#ffffff',
            marginRight: screens.xs ? '8px' : '15px',
            marginBottom: screens.xs ? '8px' : '15px',
            minWidth: screens.xs ? '160px' : '200px',
            width: screens.xs ? '100%' : 'auto',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            fontSize: screens.xs ? '14px' : '16px'
          }}>
            <strong>{branch}</strong>
            <div style={{ marginTop: '8px' }}>
              Active Students: {studentBranchStatusCounts[branch].active}
            </div>
            <div>
              Inactive Students: {studentBranchStatusCounts[branch].inactive}
            </div>
            
            {/* Total Students Tag */}
            <div
              style={{
                marginTop: '12px',
                paddingTop: '12px',
                borderTop: '1px solid rgba(255,255,255,0.2)',
                textAlign: 'center',
              }}
            >
              <Tag
                color="rgba(255,255,255,0.2)"
                style={{
                  backgroundColor: 'rgba(255,255,255,0.15)',
                  color: 'white',
                  border: '1px solid rgba(255,255,255,0.3)',
                  padding: '4px 8px',
                  fontSize: '14px',
                  fontWeight: 'bold'
                }}
              >
                Total: {studentBranchStatusCounts[branch].active + studentBranchStatusCounts[branch].inactive}
              </Tag>
            </div>
          </div>
        </Col>
      ))}
    </Row>
  );
};

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
      <Spin tip={t('loading')} />
    </div>
  );

  return (
    <div>
      <Sidebar />
      <Header />
      <div className="dashboard-content" style={{ padding: screens.xs ? '10px' : '24px' }}>
        <h1 style={{ fontSize: screens.xs ? '24px' : '32px', textAlign: screens.xs ? 'center' : 'left' }}>
          {t('userControlPage')}
        </h1>
        
        {renderRoleCounts()}
        {renderStudentBranchStatusCounts()}

        <Row justify="end" style={{ marginBottom: '16px' }}>
          <Col>
            <Button type="primary" onClick={() => {
              setNewUserData({
                firstname: '',
                lastname: '',
                nickname: '',
                email: '',
                password: '',
                role: 'admin'
              });
              setIsEditing(false);
              setEditingUserId(null);
              setCreateModalVisible(true);
            }}>
              {t('Create Admin')}
            </Button>

          </Col>
        </Row>

<Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
  <Col xs={24} sm={6}>
    <Input
      placeholder={t('searchByName')}
      value={searchTerm}
      onChange={e => setSearchTerm(e.target.value)}
      style={{ width: '100%' }}
    />
  </Col>
  <Col xs={24} sm={6}>
  <Select
    value={statusFilter}
    onChange={setStatusFilter}
    style={{ width: '100%' }}
    placeholder={t('ALL STATUS')}
    allowClear
  >
    <Option value="">{t('ALL STATUS')}</Option>
    <Option value="active">{t('active')}</Option>
    <Option value="inactive">{t('inactive')}</Option>
  </Select>
</Col>

  <Col xs={24} sm={6}>
    <Select
      value={branchFilter}
      onChange={setBranchFilter}
      style={{ width: '100%' }}
      placeholder={t('ALL BRANCHES')}
      allowClear
    >
      <Option value="">{t('ALL BRANCHES')}</Option>
      {branches.map(branch => (
        <Option key={branch.id} value={branch.name}>
          <Tag color="blue">{branch.name}</Tag>
        </Option>
      ))}
    </Select>
  </Col>
  <Col xs={24} sm={6}>
    <Select
      value={roleFilter}
      onChange={setRoleFilter}
      style={{ width: '100%' }}
      placeholder={t('ALL ROLES')}
    >
      <Option value="">{t('ALL ROLES')}</Option>
      {Object.keys(roleStyles).map(role => (
        <Option key={role} value={role}>
          <Tag color={roleStyles[role].color} style={{ color: roleStyles[role].textColor }}>
            {t(role)}
          </Tag>
        </Option>
      ))}
    </Select>
  </Col>
</Row>


        <Table
          columns={getResponsiveColumns()}
          dataSource={filteredUsers}
          rowKey="userid"
          pagination={{
            current: currentPage,
            pageSize: ITEMS_PER_PAGE,
            total: filteredUsers.length,
            onChange: setCurrentPage,
            size: screens.xs ? 'small' : 'default',
            simple: screens.xs
          }}
          scroll={{ x: true }}
          size={screens.xs ? 'small' : 'middle'}
        />
      </div>

<Modal
  title={isEditing ? t('Edit Admin') : t('Create Admin')}
  open={createModalVisible}
  onCancel={() => {
    setCreateModalVisible(false);
    setIsEditing(false);
    setEditingUserId(null);
  }}
  onOk={async () => {
    try {
      const token = localStorage.getItem('token');

      if (!newUserData.firstname || !newUserData.lastname || !newUserData.email || !newUserData.nickname) {
        notification.warning({ message: t('Please fill all required fields') });
        return;
      }

      if (!isEditing && !newUserData.password) {
        notification.warning({ message: t('Password is required for new user') });
        return;
      }

      // ✅ Require branch_id if role = admin
      if (newUserData.role === 'admin' && !newUserData.branch_id) {
        notification.warning({ message: t('Please select a branch for this admin') });
        return;
      }

      if (isEditing) {
        await axios.put(`${API_URL}/users/edit-admin/${editingUserId}`, newUserData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        notification.success({ message: t('Admin User Updated Successfully') });
      } else {
        await axios.post(`${API_URL}/users/create-admin`, newUserData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        notification.success({ message: t('Admin User Created Successfully') });
      }

      setCreateModalVisible(false);
      fetchUsers();
    } catch (error) {
      handleTokenError(error);
      notification.error({
        message: t('saveFailed'),
        description: error.response?.data?.message || 'Unexpected error occurred',
      });
    }
  }}
  okText={isEditing ? t('Save') : t('Create')}
>
  <Input
    style={{ marginBottom: 12 }}
    placeholder={t('First name')}
    value={newUserData.firstname}
    onChange={(e) => setNewUserData({ ...newUserData, firstname: e.target.value })}
  />
  <Input
    style={{ marginBottom: 12 }}
    placeholder={t('Last name')}
    value={newUserData.lastname}
    onChange={(e) => setNewUserData({ ...newUserData, lastname: e.target.value })}
  />
  <Input
    style={{ marginBottom: 12 }}
    placeholder={t('Nickname')}
    value={newUserData.nickname}
    onChange={(e) => setNewUserData({ ...newUserData, nickname: e.target.value })}
  />
  <Input
    style={{ marginBottom: 12 }}
    placeholder={t('Email')}
    value={newUserData.email}
    onChange={(e) => setNewUserData({ ...newUserData, email: e.target.value })}
  />
  <Input.Password
    style={{ marginBottom: 12 }}
    placeholder={t('Password')}
    value={newUserData.password}
    onChange={(e) => setNewUserData({ ...newUserData, password: e.target.value })}
  />
  <Select
    style={{ width: '100%', marginBottom: 12 }}
    value={newUserData.role}
    onChange={(value) => setNewUserData({ ...newUserData, role: value })}
  >
    <Option value="admin">{t('admin')}</Option>
    <Option value="superadmin">{t('superadmin')}</Option>
  </Select>

  {/* ✅ Branch Selector (only visible when creating/editing Admins) */}
  {newUserData.role === 'admin' && (
    <Select
      placeholder={t('Select Branch')}
      style={{ width: '100%' }}
      value={newUserData.branch_id}
      onChange={(value) => setNewUserData({ ...newUserData, branch_id: value })}
    >
      {branches.map(branch => (
        <Option key={branch.id} value={branch.id}>
          {branch.name}
        </Option>
      ))}
    </Select>
  )}
</Modal>



    </div>
  );
};

export default withRoleAccess(UserControlPage, ['superadmin']);
